import { Injectable } from '@angular/core';
import { TranslationService } from './translation.service';

export interface FacePosition {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

export interface FaceDirection {
  forehead?: number;
  chin?: number;
  pitch?: number;
  roll?: number;
  yaw?: number;
}

@Injectable({
  providedIn: 'root',
})
export class FaceStabilizationService {
  // Hold is TIME based (was 30 detection frames, i.e. ~1s on a fast phone and
  // ~3s+ on a slow one). During the hold every frame is sampled and the
  // stillest one is uploaded (StillFrameSelectorService), so a longer hold =
  // more candidates = sharper result, not just a longer wait.
  private readonly HOLD_MS = 5000;
  private readonly POSITION_TOLERANCE = 0.005;
  private readonly HORIZONTAL_TOLERANCE = 0.005;
  private readonly YAW_TOLERANCE = 1.2;
  private readonly PITCH_TOLERANCE = 1.2;

  // Desktop-webcam relaxations (landscape web only — see CameraComponent). A
  // fixed-distance webcam in a 16:9 frame cannot hold the strict portrait
  // position box + 30-frame hold simultaneously (the size floor and the box
  // fight each other -> infinite "move closer"/"move lower" loop). Phones keep
  // the strict values below; relaxed mode widens the acceptance envelope so the
  // intersection is actually reachable. The crop still centers the face and
  // yaw/roll still enforce frontality, so analysis quality holds.
  private relaxed = false;
  private readonly RELAXED_HOLD_MS = 3000;
  private readonly RELAXED_POSITION_TOLERANCE = 0.012;
  private readonly RELAXED_HORIZONTAL_TOLERANCE = 0.012;
  private readonly RELAXED_YAW_TOLERANCE = 2.0;
  private readonly RELAXED_PITCH_TOLERANCE = 2.0;

  setRelaxedFraming(relaxed: boolean): void {
    this.relaxed = relaxed;
  }

  private lastFacePosition: FacePosition = {
    x: 0,
    y: 0,
    z: 0,
    yaw: 0,
    pitch: 0,
  };
  // performance.now() when the current uninterrupted in-tolerance run began;
  // 0 = not holding.
  private stableSince = 0;
  // Last checkStability() time: the per-frame tolerances are defined for a
  // ~15-30 fps loop; a slow phone (3-6 fps during the hold) accumulates the
  // same natural micro-motion over a longer interval and would be held to a
  // stricter standard, so the tolerance grows with sqrt(dt), capped at 2.5x.
  private lastCheckAt = 0;
  private readonly TOL_REF_MS = 66;
  private readonly TOL_MAX_SCALE = 2.5;

  constructor(private translationService: TranslationService) {
  }

  resetStability(): void {
    this.stableSince = 0;
  }

  // True from the first in-tolerance frame on (elapsed may still be 0ms).
  isHolding(): boolean {
    return this.stableSince > 0;
  }

  // Milliseconds of uninterrupted hold so far (0 when not holding).
  getHoldElapsedMs(): number {
    return this.stableSince ? performance.now() - this.stableSince : 0;
  }

  getHoldRequiredMs(): number {
    return this.relaxed ? this.RELAXED_HOLD_MS : this.HOLD_MS;
  }

  checkStability(faceLandmarks: any, faceDirection: FaceDirection): boolean {
    if (!faceLandmarks) return false;

    const nowMs = performance.now();
    const dt = this.lastCheckAt ? nowMs - this.lastCheckAt : this.TOL_REF_MS;
    this.lastCheckAt = nowMs;
    const tolScale = Math.min(
      this.TOL_MAX_SCALE,
      Math.max(1, Math.sqrt(dt / this.TOL_REF_MS))
    );

    const hTol =
      (this.relaxed ? this.RELAXED_HORIZONTAL_TOLERANCE : this.HORIZONTAL_TOLERANCE) *
      tolScale;
    const posTol =
      (this.relaxed ? this.RELAXED_POSITION_TOLERANCE : this.POSITION_TOLERANCE) *
      tolScale;
    const yawTol =
      (this.relaxed ? this.RELAXED_YAW_TOLERANCE : this.YAW_TOLERANCE) * tolScale;
    const pitchTol =
      (this.relaxed ? this.RELAXED_PITCH_TOLERANCE : this.PITCH_TOLERANCE) * tolScale;
    const requiredMs = this.getHoldRequiredMs();
    // Horizontal-centering band for the nose: ±0.05 strict, ±0.10 relaxed.
    const centerHalfBand = this.relaxed ? 0.1 : 0.05;

    const noseTip = faceLandmarks[1];
    const currentPosition: FacePosition = {
      x: noseTip.x,
      y: noseTip.y,
      z: noseTip.z,
      yaw: faceDirection.yaw,
      pitch: faceDirection.pitch,
    };

    // First frame
    if (this.lastFacePosition.x === 0 && this.lastFacePosition.y === 0) {
      this.lastFacePosition = currentPosition;
      return false;
    }

    // Check horizontal movement separately
    const horizontalMovement = Math.abs(
      currentPosition.x - this.lastFacePosition.x,
    );
    if (horizontalMovement > hTol) {
      this.stableSince = 0;
      this.lastFacePosition = currentPosition;
      return false;
    }

    // Check vertical and depth movement
    const verticalAndDepthMovement = Math.sqrt(
      Math.pow(currentPosition.y - this.lastFacePosition.y, 2) +
      Math.pow(currentPosition.z - this.lastFacePosition.z, 2),
    );

    const yawChange = Math.abs(currentPosition.yaw - this.lastFacePosition.yaw);
    const pitchChange = Math.abs(
      currentPosition.pitch - this.lastFacePosition.pitch,
    );

    // Update last position before other checks
    this.lastFacePosition = currentPosition;

    // If movement is too large, reset immediately
    if (
      verticalAndDepthMovement > posTol ||
      yawChange > yawTol ||
      pitchChange > pitchTol
    ) {
      this.stableSince = 0;
      return false;
    }

    // Check if face is centered
    if (
      noseTip.x > 0.5 + centerHalfBand ||
      noseTip.x < 0.5 - centerHalfBand
    ) {
      this.stableSince = 0;
      return false;
    }

    // If we got here, position is stable and centered
    if (!this.stableSince) this.stableSince = performance.now();
    return performance.now() - this.stableSince >= requiredMs;
  }

  // The same bounds isPositionValid enforces, per axis, for the guide colours:
  // centered (nose left/right), box (distance + height), tilt (chin up/down),
  // turn (face turned). All four true == isPositionValid().isValid.
  positionAxes(
    faceDirection: FaceDirection,
    faceLandmarks: any,
  ): { centered: boolean; box: boolean; tilt: boolean; turn: boolean } {
    const H_TOL = this.relaxed ? 0.1 : 0.05;
    const FOREHEAD_MIN = this.relaxed ? 0.12 : 0.24;
    const FOREHEAD_MAX = this.relaxed ? 0.4 : 0.32;
    const CHIN_MIN = this.relaxed ? 0.7 : 0.78;
    const CHIN_MAX = this.relaxed ? 0.97 : 0.86;
    const MAX_YAW = this.relaxed ? 14 : 10;
    const MAX_ROLL = this.relaxed ? 14 : 10;
    const noseX: number | undefined = faceLandmarks?.[1]?.x;
    const centered = typeof noseX === 'number' ? Math.abs(noseX - 0.5) <= H_TOL : false;
    const f = faceDirection.forehead ?? NaN;
    const c = faceDirection.chin ?? NaN;
    const box = f >= FOREHEAD_MIN && f <= FOREHEAD_MAX && c >= CHIN_MIN && c <= CHIN_MAX;
    const tilt = Math.abs(faceDirection.roll ?? 0) <= MAX_ROLL;
    const turn = Math.abs(faceDirection.yaw ?? 0) <= MAX_YAW;
    return { centered, box, tilt, turn };
  }

  isPositionValid(
    faceDirection: FaceDirection,
    faceLandmarks: any,
  ): {
    isValid: boolean;
    error?: string;
    position?: { forehead: string | null; chin: string | null };
  } {
    // Relaxed (desktop landscape webcam) widens every bound so the size floor and
    // the vertical position box stop fighting each other; phones keep the strict
    // box. forehead/chin are normalized landmark Y; the allowed face vertical span
    // goes from [0.5,0.7] strict to [0.30,0.85] relaxed.
    const H_CENTER = 0.5;
    const H_TOL = this.relaxed ? 0.1 : 0.05;

    // Phones: forehead (landmark 10 = top of the forehead skin, not the
    // hairline) must sit 24-32% down the frame and the chin 78-86%, i.e. the
    // face spans 46-62% of the frame height. The old 20-30 / 80-90 let her
    // come close enough to cut the hairline while still reading green.
    const FOREHEAD_MIN = this.relaxed ? 0.12 : 0.24;
    const FOREHEAD_MAX = this.relaxed ? 0.4 : 0.32;

    const CHIN_MIN = this.relaxed ? 0.7 : 0.78;
    const CHIN_MAX = this.relaxed ? 0.97 : 0.86;

    const MAX_YAW = this.relaxed ? 14 : 10;
    const MAX_ROLL = this.relaxed ? 14 : 10;

    const noseX: number | undefined = faceLandmarks?.[1]?.x;
    if (typeof noseX === 'number' && Math.abs(noseX - H_CENTER) > H_TOL) {
      const error = noseX > H_CENTER ? 
        this.translationService.translate('camera.positioning.moveHeadRight') : 
        this.translationService.translate('camera.positioning.moveHeadLeft');
      return { isValid: false, error, position: { forehead: null, chin: null } };
    }

    const foreheadDir: 'up' | 'down' | null =
      faceDirection.forehead < FOREHEAD_MIN ? 'down' :
        faceDirection.forehead > FOREHEAD_MAX ? 'up' : null;

    const chinDir: 'up' | 'down' | null =
      faceDirection.chin > CHIN_MAX ? 'up' :
        faceDirection.chin < CHIN_MIN ? 'down' : null;

    const position: { forehead: 'up' | 'down' | null; chin: 'up' | 'down' | null } = {
      forehead: foreheadDir,
      chin: chinDir,
    };

    if (foreheadDir === 'down' && chinDir === 'up') {
      return { isValid: false, error: this.translationService.translate('camera.positioning.faceTooClose'), position };
    }
    if (foreheadDir === 'up' && chinDir === 'down') {
      return { isValid: false, error: this.translationService.translate('camera.positioning.faceTooFar'), position };
    }

    if (foreheadDir && foreheadDir === chinDir) {
      const error = foreheadDir === 'up' ? 
        this.translationService.translate('camera.positioning.moveHeadHigher') : 
        this.translationService.translate('camera.positioning.moveHeadLower');
      return { isValid: false, error, position };
    }

    if (foreheadDir === 'down') {
      return { isValid: false, error: this.translationService.translate('camera.positioning.moveUpAndBack'), position };
    }
    if (foreheadDir === 'up') {
      return { isValid: false, error: this.translationService.translate('camera.positioning.moveDownAndCloser'), position };
    }
    if (chinDir === 'down') {
      return { isValid: false, error: this.translationService.translate('camera.positioning.moveUpAndCloser'), position };
    }
    if (chinDir === 'up') {
      return { isValid: false, error: this.translationService.translate('camera.positioning.moveDownAndBack'), position };
    }

    if (Math.abs(faceDirection.yaw) > MAX_YAW) {
      return { isValid: false, error: this.translationService.translate('camera.positioning.faceDirectly'), position };
    }
    if (faceDirection.roll > MAX_ROLL) {
      return { isValid: false, error: this.translationService.translate('camera.positioning.tiltChinDown'), position };
    }
    if (faceDirection.roll < -MAX_ROLL) {
      return { isValid: false, error: this.translationService.translate('camera.positioning.tiltChinUp'), position };
    }

    return { isValid: true, position };
  }
}
